/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type TtbPaymentModuleSpecsFragment } from '../../../../api/fragments/TtbPaymentModuleSpecs';
import { type TtbPaymentSettingsSpecFragment } from '../../../../api/fragments/TtbPaymentSettingsSpec';
import {
    type UpdateTtbPaymentSettingsMutation,
    type UpdateTtbPaymentSettingsMutationVariables,
    UpdateTtbPaymentSettingsDocument,
} from '../../../../api/mutations/updateTtbPaymentSettings';
import { type TtbPaymentUpdateSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type FormValues = TtbPaymentUpdateSettingsInput;

export type UpdateTtbPaymentSettingInnerPageProps = {
    module: TtbPaymentModuleSpecsFragment;
    paymentSettings: TtbPaymentSettingsSpecFragment & {
        signingPublicKey?: string;
        signingPrivateKey?: string;
        encryptionPublicKey?: string;
        encryptionPrivateKey?: string;
    };
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdateTtbPaymentSettingInnerPage = ({ module, paymentSettings }: UpdateTtbPaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const paymentSettingsId = paymentSettings.id;

    const { t } = useTranslation(['ttbPaymentModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const { ttbFlag3dsOptions } = useSystemOptions();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id', 'paymentModuleId'], paymentSettings),
        }),
        [paymentSettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('ttbPaymentModuleDetails:paymentSettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateTtbPaymentSettingsMutation, UpdateTtbPaymentSettingsMutationVariables>({
                    mutation: UpdateTtbPaymentSettingsDocument,
                    variables: { moduleId, id: paymentSettingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('ttbPaymentModuleDetails:paymentSettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, paymentSettingsId, navigate]
    );

    const validate = useValidator(validator(false));

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="paymentSettingsForm" htmlType="submit" type="primary">
                    {t('ttbPaymentModuleDetails:paymentSettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('ttbPaymentModuleDetails:paymentSettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="paymentSettingsForm"
                        layout="vertical"
                        name="paymentSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('ttbPaymentModuleDetails:dividers.mainDetails')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.displayName', {
                                                returnObjects: true,
                                            })}
                                            name="displayName"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                                                returnObjects: true,
                                            })}
                                            name="currency"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                                                returnObjects: true,
                                            })}
                                            name="amount"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('ttbPaymentModuleDetails:dividers.paymentSettings')}>
                                <Row>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.audience', {
                                                returnObjects: true,
                                            })}
                                            name="audience"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SelectField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.flag3ds', {
                                                returnObjects: true,
                                            })}
                                            name="flag3ds"
                                            options={ttbFlag3dsOptions}
                                            showSearch
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <PasswordField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', {
                                                returnObjects: true,
                                            })}
                                            name="apiKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.officeId', {
                                                returnObjects: true,
                                            })}
                                            name="officeId"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t(
                                                'ttbPaymentModuleDetails:paymentSettingsDrawer.fields.paymentEndpoint',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="paymentEndpoint"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SingleSecretUploadField
                                            {...t(
                                                'ttbPaymentModuleDetails:paymentSettingsDrawer.fields.signingPrivateKey',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="signingPrivateKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SingleSecretUploadField
                                            {...t(
                                                'ttbPaymentModuleDetails:paymentSettingsDrawer.fields.signingPublicKey',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="signingPublicKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SingleSecretUploadField
                                            {...t(
                                                'ttbPaymentModuleDetails:paymentSettingsDrawer.fields.encryptionPrivateKey',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="encryptionPrivateKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SingleSecretUploadField
                                            {...t(
                                                'ttbPaymentModuleDetails:paymentSettingsDrawer.fields.encryptionPublicKey',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="encryptionPublicKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('ttbPaymentModuleDetails:paymentSettingsDrawer.fields.kid', {
                                                returnObjects: true,
                                            })}
                                            name="kid"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateTtbPaymentSettingInnerPage;
